import {BasketPromotionsInput, Image, ProductVariant, Unit, type VariantIdentifier} from "@/shared/graphql/types";
import {ClientLineItem, ClientLineItemType, ProductVariantType} from "@/shared/services/basket/ClientLineItem.ts";
import {v4 as uuidv4} from "uuid";
import {ClientBasket} from "@/shared/services/basket/ClientBasket.ts";
import {BasketState, useBasketStore} from "@/shared/stores/basket/basket.store.ts";
import {watch, WatchStopHandle} from 'vue';
import basketService from "@/shared/services/basket/basket.service.ts";

export type ClientProductVariant = {
    id: string
    code: string
    name: string
    image: Image
}
//twee types maken, BasketUpdate en BasketAdd → bij add clientProductVariant bij het andere gewoon de id van het item dat aangepast moet worden
export type BasketAddModification = {
    variant: ProductVariant | ClientProductVariant
    quantity: number
    unit?: string
}
export type BasketUpdateModification = {
    id: String
    quantity: number
    unit?: string
}
export type BasketModificationEmit = {
    variant: ProductVariant | ClientProductVariant
    quantity: number
    unit?: Unit
}

export class BasketEvent {
    readonly type: string
    readonly name: string

    constructor(type: string, name: string) {
        this.type = type
        this.name = name
    }
}

export type BasketAction = {
    event: BasketItemEvent
    items: ClientLineItem[]
    promotionsInput: BasketPromotionsInput
}
export type BasketQuantity = {
    variant: string
    unit: string
    quantity: number
}

export class BasketItemEvent extends BasketEvent {
    readonly code: string
    readonly quantity: number

    constructor(type: string, name: string, code: string, quantity: number) {
        super(type, name);
        this.code = code
        this.quantity = quantity
    }
}

export function cloneItems(basket: ClientBasket): ClientLineItem[] {
    return basket.items.filter((item) => item.type === ClientLineItemType.Variant || (item.type === ClientLineItemType.Promotion && item.promotion.quantityLinked === false))
        .map((item: ClientLineItem) => {
            if (!item.identifier) {
                console.error("Item without identifier during cloneItems", {itemContent: item});
            }
            const newItem = new ClientLineItem(
                item.id,
                {kind: item.identifier.kind, value: item.identifier.value} as VariantIdentifier,
                item.type,
                item.variantType,
                item.code,
                item.name,
                item.unit,
                item.quantity,
                item.unitQuantity,
                1,
                item.remark,
                item.image,
                item.unitPrice,
                item.totalPrice,
                item.errors,
                item.discount,
                item.discountItems,
            )
            newItem.promotion = item.promotion
            return newItem
        });
}

export function createItem(variant: ProductVariant | ClientProductVariant, quantity: number, unit?: string): ClientLineItem {
    // typing check assumed that ProductVariant has a 'value' property in 'code'
    //@ts-ignore
    let isClientProductVariant = variant?.id === undefined;
    let variantCode: string;
    let variantName: string;
    let variantType: ProductVariantType;
    let variantImage: Image | undefined;
    let identifier: VariantIdentifier;

    if (isClientProductVariant) {
        variantCode = (variant as ClientProductVariant).code;
        variantName = (variant as ClientProductVariant).name;
        variantType = ProductVariantType.Product;
        variantImage = (variant as ClientProductVariant).image;
        identifier = {kind: 'CODE', value: variant.code} as VariantIdentifier
    } else {
        variantCode = (variant as ProductVariant).code.value;
        variantName = (variant as ProductVariant).name.actual;
        variantType = (variant as ProductVariant).type;
        variantImage = (variant as ProductVariant).featuredImage;
        identifier = {kind: 'ID', value: variant.id} as VariantIdentifier
    }

    return new ClientLineItem(
        uuidv4().toString(),
        identifier,
        ClientLineItemType.Variant,
        variantType,
        variantCode,
        variantName,
        {code: "", label: "", name: "", plural: "", supportsDecimals: false, id: unit},
        quantity,
        null,
        1,
        "",
        variantImage
    )
}

export function isBasketUpdateModification(modification: BasketAddModification | BasketUpdateModification): modification is BasketUpdateModification {
    return (modification as BasketUpdateModification).id !== undefined;
}

/**
 * Resolves as soon as the basket store is not busy and not updating.
 * If the store is already idle the promise resolves immediately.
 */
/**
 * Resolves as soon as the basket store is not busy and not updating.
 * If the store is already idle the promise resolves immediately.
 * Throws an error if the basket doesn't become ready within 30 seconds.
 */
export function waitForBasketReady(store: BasketState): Promise<void> {
    if (!store.busy && !store.updating) return Promise.resolve();

    // Otherwise wait until both flags turn to false
    return new Promise<void>((resolve, reject) => {
        let stop: WatchStopHandle;
        let timeoutId: any;

        // Set up the timeout
        timeoutId = setTimeout(() => {
            stop?.();
            reject(new Error("Basket can not be loaded"));
        }, 30000); // 30 seconds

        stop = watch(
            () => [store.busy, store.updating],
            ([busy, updating]) => {
                if (!busy && !updating) {
                    clearTimeout(timeoutId);
                    stop();         // stop watching – we're done
                    resolve();
                }
            },
            {immediate: true} // react instantly to the current values
        );
    });
}

export const initGuestBasket = async (guestId: string) => {
    const basketStore = useBasketStore();
    await basketStore.manage(async () => await basketService.loadGuestBasket(guestId))
}
export const initCustomerBasket = async (customer: string, contact: string) => {
    const basketStore = useBasketStore();
    if (basketStore.basket) return //Basket has already been loaded
    await basketStore.manage(async () => await basketService.loadBasket(contact))
}
